import { useEffect, useRef, useState } from 'react';
import BreakdownTip from './BreakdownTip';
import CardOverlay from './CardOverlay';
import { IconText } from './Amount';
import { useHover } from '../utils/Mobile';
import { useImages } from '../context/ImageContext';

// The boards in the middle of the table, under the card row, for everyone to see: culture here (points and rating, as
// on the culture board), and the military board (MilitaryArea.js). Their tracks show the spaces the players are on and
// as many around them as fit, with the empty stretches that don't fit cut out (⋯), so a spread-out game takes no more
// room than a close one.

// How wide the parts of a track are, in pixels (as index.css draws them): a space, wider for each token past the first
// (a token and the gap beside it, plus the space's padding and border), and a cut
const SpaceWidth = (tokens) => Math.max(24, tokens * 14 + 6);
const CutWidth = 22;

// How wide a track showing these spaces is: each space, and a cut before each one not next to the one before it (none
// before the first: the track simply starts there)
const TrackWidth = (shown, tokens) => shown.reduce((width, space, index) =>
    width + SpaceWidth(tokens[space] ?? 0) + (index > 0 && space > shown[index - 1] + 1 ? CutWidth : 0), 0);

// The spaces to show, in order, for the players on these spaces (tokens: how many on each), in room pixels. Everyone's
// space, then the empty spaces between two of them, closest pairs first, as long as the whole stretch fits (so players
// close together share a stretch, and far ones keep their cut); then, while any fits, one more space after and before
// each stretch in turn, to give each the look of the board around it (reaching the next stretch joins them, saving the cut).
export function ShownSpaces(tokens, room)
{
    const occupied = Object.keys(tokens).map(Number).sort((a, b) => a - b);
    let shown = [...occupied];
    const fits = (spaces) => TrackWidth(spaces, tokens) <= room;
    const sorted = (spaces) => [...new Set(spaces)].sort((a, b) => a - b);
    const pairs = occupied.slice(1).map((space, index) => [occupied[index], space]).sort((a, b) => (a[1] - a[0]) - (b[1] - b[0]));
    for (const [from, to] of pairs)
    {
        const joined = sorted([...shown, ...Array.from({ length: to - from - 1 }, (_, n) => from + 1 + n)]);
        if (fits(joined))
        {
            shown = joined;
        }
    }
    for (let grew = true; grew;)
    {
        grew = false;
        // (the stretches as they are now: their first and last spaces)
        const stretches = shown.reduce((runs, space) => {
            const last = runs[runs.length - 1];
            last && last.to === space - 1 ? last.to = space : runs.push({ from: space, to: space });
            return runs;
        }, []);
        for (const stretch of stretches)
        {
            for (const space of [stretch.to + 1, stretch.from - 1])
            {
                const more = sorted([...shown, space]);
                if (space >= 0 && !shown.includes(space) && fits(more))
                {
                    shown = more;
                    grew = true;
                }
            }
        }
    }
    return shown;
}

// One track: its label (an icon, with a word or a sign before it if any; Title on hover), then the spaces shown (see ShownSpaces), each with its number and a token
// for each player on it, side by side (a space shared by several is wider, to hold them all). Spaces next to each other
// on the board touch; otherwise ⋯ stands for the spaces between (the track starts at its first space, with no ⋯ before it).
// Markers: [{ player, name, value, breakdown }], a space's tokens in this order; step: every step-th space is shaded, as on
// the board. Current: the player whose turn it is, their token lit up.
export function ScoreTrack({Label, Prefix, Title, Icon, Markers, Colors, step = 5, Current})
{
    const { images } = useImages();
    const track = useRef(null);
    const [room, setRoom] = useState(0);
    useEffect(() => {
        // (inside its padding)
        const inside = (element) => element.clientWidth - parseFloat(getComputedStyle(element).paddingLeft) - parseFloat(getComputedStyle(element).paddingRight);
        const measure = () => setRoom(track.current ? inside(track.current) : 0);
        // (measured at once too, not only when the observer first reports, which a page in the background can put off)
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(track.current);
        return () => observer.disconnect();
    }, []);
    const tokens = Markers.reduce((counts, marker) => ({ ...counts, [marker.value]: (counts[marker.value] ?? 0) + 1 }), {});
    // (until the track's width is known: only the players' spaces)
    const shown = room > 0 ? ShownSpaces(tokens, room) : Object.keys(tokens).map(Number).sort((a, b) => a - b);
    return (
        <div className="tta-track-row">
            <span className="tta-track-label" title={Title}>{Prefix}{images[Icon] && <img src={images[Icon].src} alt=""/>}{Label}</span>
            <div className="tta-track" ref={track}>
                {shown.map((space, index) => {
                    const before = index > 0 ? shown[index - 1] + 1 : space;
                    const here = Markers.filter(marker => marker.value === space);
                    // (every step-th space shaded, as on the board; every other one of those darker)
                    const kind = space % (2 * step) === 0 ? ' tta-track-ten' : space % step === 0 ? ' tta-track-five' : '';
                    return [
                        space > before && <span key={`cut${space}`} className="tta-track-gap"
                            title={space - before === 1 ? 'A space with no one on it' : `${space - before} spaces with no one on them`}>⋯</span>,
                        <div key={space} className={'tta-track-space' + kind} style={{ flexBasis: SpaceWidth(here.length) }}>
                            <span className="tta-track-number">{space}</span>
                            <span className="tta-track-tokens">
                                {here.map(marker => {
                                    const style = { backgroundColor: Colors[marker.player] };
                                    const className = 'tta-track-token' + (marker.player === Current ? ' current' : '');
                                    const label = `${marker.name}: ${marker.value}`;
                                    // (where it comes from on hover, when it comes from somewhere: not culture points, simply scored)
                                    return marker.breakdown
                                        ? <BreakdownTip key={marker.player} As="span" className={className} style={style} label={label} breakdown={marker.breakdown}/>
                                        : <span key={marker.player} className={className} style={style} title={label}/>;
                                })}
                            </span>
                        </div>,
                    ];
                })}
            </div>
        </div>
    );
}

// A leader's lasting effect on the table (Alfred Nobel's prize, Pierre de Coubertin's Olympic Games): his picture, and
// on hover his card with what the effect does in this game under it
export function TablePortrait({Image, Card, Effect})
{
    const { images } = useImages();
    const [showOverlay, setShowOverlay, hoverProps] = useHover();
    const portrait = useRef(null);
    return <>
        <div ref={portrait} className="tta-table-portrait" {...hoverProps}>
            {images[Image] ? <img src={images[Image].src} alt={Card.name}/> : Card.name}
        </div>
        <CardOverlay ShowOverlay={showOverlay} ComponentRef={portrait} card={Card} callback={() => {}}
            OnClose={() => setShowOverlay(false)} Effect={<IconText text={Effect}/>}/>
    </>;
}

// The culture board: everyone's culture points, and their culture rating (what they score each turn); beside them, Alfred
// Nobel's prize while it is in play (Nobel: the game's nobelPrize). Current: the player whose turn it is. Players tied on a
// space are in turn order from the current player, as on the military track, the first of them rightmost.
export function CultureBoard({Players, Colors, Resigned, Nobel, Current})
{
    const after = (index) => (index - (Current ?? 0) + Players.length) % Players.length;
    const playing = Players.map((player, index) => ({ player, index })).filter(({ index }) => !Resigned?.[index])
        .sort((a, b) => after(b.index) - after(a.index));
    return (
        <div className="tta tta-left tta-table-board tta-culture-board">
            <div className="tta-culture-tracks">
                <ScoreTrack Title="Culture points" Icon="Culture" Colors={Colors} step={5} Current={Current}
                    Markers={playing.map(({ player, index }) => ({ player: index, name: player.name, value: player.culture }))}/>
                <ScoreTrack Prefix="+" Title="Culture each turn" Icon="Culture" Colors={Colors} step={5} Current={Current}
                    Markers={playing.map(({ player, index }) => ({ player: index, name: player.name, value: player.cultureProduction,
                        breakdown: player.breakdowns.culture }))}/>
            </div>
            {Nobel && <TablePortrait Image="Nobel" Card={Nobel.card}
                Effect="Nobel Prize: +4 culture at the end of a turn in which a civilization developed 2 or more technologies"/>}
        </div>
    );
}
